import { css } from '@emotion/react';
import { Box, COLOR_PALETTE, Icon, Text, Title } from '@inflearn/ds-react-next';
import type { IconDefinition } from '@inflearn/fontawesome-svg-core';
import {
  faClipboardListCheck,
  faComments,
  faFilePen,
  faUserGroup,
} from '@inflearn/pro-regular-svg-icons';
import { useTranslation } from 'next-i18next';

import { getInterviewerImageSrc } from '../hero/interviewers';

import { LANDING_COLOR, LANDING_FONT, LANDING_MOBILE_MQ, LANDING_SPACE } from './constants';
import { FEATURE_SCREEN_SRC } from './featureScreens';

const MUTE_CARD_BG = '#16191D';
const MUTE_LABEL_COLOR = COLOR_PALETTE.rallit[6];

type FeatureCard = {
  key: string;
  background: string;
  labelColor: string;
  labelKey: string;
  labelDefault: string;
  titleKey1: string;
  titleDefault1: string;
  titleKey2: string;
  titleDefault2: string;
  descKeys: string[];
  descDefaults: string[];
  icon: IconDefinition;
  /** 임시 — CDN/실제 캡처 교체 전 플레이스홀더 라벨 */
  screenLabel: string;
  /** 스크린 영역 이미지 — 없으면 screenLabel 플레이스홀더 */
  screenSrc?: string;
  avatarSrc?: string;
  bubbleKey?: string;
  bubbleDefault?: string;
  pills?: Array<{ label: string; color: string; bg: string }>;
};

const CARDS: FeatureCard[] = [
  {
    key: 'interviewer',
    background: MUTE_CARD_BG,
    labelColor: MUTE_LABEL_COLOR,
    labelKey: 'ai_interviews.landing.features.q4.label',
    labelDefault: '면접관 선택',
    titleKey1: 'ai_interviews.landing.features.q4.title1',
    titleDefault1: '상황에 맞는',
    titleKey2: 'ai_interviews.landing.features.q4.title2',
    titleDefault2: '면접관을 골라요.',
    descKeys: ['ai_interviews.landing.features.q4.d1', 'ai_interviews.landing.features.q4.d2'],
    descDefaults: [
      '실전에서 만날 유형을 미리 골라 대비하세요.',
      '성향이 다르면 질문 스타일이 완전히 달라져요.',
    ],
    icon: faUserGroup,
    screenLabel: '면접관 선택 화면',
    screenSrc: FEATURE_SCREEN_SRC.interviewerPick,
  },
  {
    key: 'questions',
    background: MUTE_CARD_BG,
    labelColor: MUTE_LABEL_COLOR,
    labelKey: 'ai_interviews.landing.features.q1.label',
    labelDefault: '나를 위한 질문',
    titleKey1: 'ai_interviews.landing.features.q1.title1',
    titleDefault1: '내 이력서로 만드는',
    titleKey2: 'ai_interviews.landing.features.q1.title2',
    titleDefault2: '맞춤형 면접 질문',
    descKeys: [
      'ai_interviews.landing.features.q1.d1',
      'ai_interviews.landing.features.q1.d2',
      'ai_interviews.landing.features.q1.d3',
    ],
    descDefaults: [
      'AI 면접관이 이력서 내용을 분석해',
      '실제 면접처럼 맞춤형 질문을 만들어요.',
      '내가 한 일에서 나온 질문으로 연습해요.',
    ],
    icon: faFilePen,
    screenLabel: '맞춤형 면접 질문 화면',
    screenSrc: FEATURE_SCREEN_SRC.questions,
  },
  {
    key: 'interview',
    background: MUTE_CARD_BG,
    labelColor: MUTE_LABEL_COLOR,
    labelKey: 'ai_interviews.landing.features.q2.label',
    labelDefault: '면접 완벽 재현',
    titleKey1: 'ai_interviews.landing.features.q2.title1',
    titleDefault1: '내 답변에 따라 달라지는',
    titleKey2: 'ai_interviews.landing.features.q2.title2',
    titleDefault2: 'AI 면접관과의 대화',
    descKeys: [
      'ai_interviews.landing.features.q2.d1',
      'ai_interviews.landing.features.q2.d2',
      'ai_interviews.landing.features.q2.d3',
    ],
    descDefaults: [
      '내 답변을 바탕으로 이어지는 꼬리질문과',
      '피드백으로 실전 면접의 흐름을 완벽히',
      '체험할 수 있어요.',
    ],
    icon: faComments,
    screenLabel: 'AI 면접관과의 대화 화면',
    screenSrc: FEATURE_SCREEN_SRC.interview,
  },
  {
    key: 'report',
    background: MUTE_CARD_BG,
    labelColor: MUTE_LABEL_COLOR,
    labelKey: 'ai_interviews.landing.features.q3.label',
    labelDefault: '면접 후 피드백',
    titleKey1: 'ai_interviews.landing.features.q3.title1',
    titleDefault1: '뭘 고쳐야 할지',
    titleKey2: 'ai_interviews.landing.features.q3.title2',
    titleDefault2: '알고 나가는 리포트',
    descKeys: [
      'ai_interviews.landing.features.q3.d1',
      'ai_interviews.landing.features.q3.d2',
      'ai_interviews.landing.features.q3.d3',
    ],
    descDefaults: [
      '질문마다 좋았던 점, 아쉬운 점,',
      '다음엔 이렇게 답해보자는 개선안까지.',
      '완성되면 카카오톡으로 알려드려요.',
    ],
    icon: faClipboardListCheck,
    screenLabel: '면접 후 피드백 화면',
    screenSrc: FEATURE_SCREEN_SRC.report,
    avatarSrc: getInterviewerImageSrc('dora'),
    bubbleKey: 'ai_interviews.landing.features.q3.bubble',
    bubbleDefault: '근거를 수치로 말한 부분이 설득력 있었어요',
    pills: [
      { label: 'GOOD', color: COLOR_PALETTE.rallit[6], bg: 'rgba(0,204,170,.14)' },
      { label: 'BAD', color: '#C92A2A', bg: 'rgba(250,82,82,.12)' },
      { label: 'NEXT', color: '#3B5BDB', bg: 'rgba(76,95,215,.12)' },
    ],
  },
];

export const FeatureStack = () => {
  const { t } = useTranslation();

  return (
    <Box component="section" css={styles.section} data-reveal>
      <Box css={styles.cards}>
        {/* TODO: 실제 타이틀 카피로 교체 */}
        <Title order={3} css={styles.cardsTitle}>
          이렇게 연습해요
        </Title>
        <Box css={styles.cardList}>
          {CARDS.map((card) => {
            return (
              <Box key={card.key} css={styles.card} style={{ background: card.background }}>
                <Box css={styles.cardCopy}>
                  <Text css={styles.cardLabel} style={{ color: card.labelColor }}>
                    {t(card.labelKey, card.labelDefault)}
                  </Text>
                  <Title order={4} css={styles.cardTitle}>
                    {t(card.titleKey1, card.titleDefault1)}
                    <br />
                    {t(card.titleKey2, card.titleDefault2)}
                  </Title>
                  <Text css={styles.cardDesc}>
                    {card.descDefaults.map((descDefault, index) => (
                      <span key={card.descKeys[index]}>
                        {index > 0 ? <br /> : null}
                        {t(card.descKeys[index], descDefault)}
                      </span>
                    ))}
                  </Text>
                  {card.pills ? (
                    <Box css={styles.pills}>
                      {card.pills.map((pill) => (
                        <Text
                          key={pill.label}
                          component="span"
                          css={styles.pill}
                          style={{ color: pill.color, background: pill.bg }}
                        >
                          {pill.label}
                        </Text>
                      ))}
                    </Box>
                  ) : null}
                </Box>

                <Box css={styles.screenWrap}>
                  <Box css={styles.iconBadge} aria-hidden>
                    <Icon icon={card.icon} size={22} color="#fff" />
                  </Box>
                  <Box css={styles.screen} aria-label={card.screenLabel}>
                    {card.screenSrc ? (
                      // eslint-disable-next-line @next/next/no-img-element -- FeatureStack 스크린, 어드민 CDN
                      <img
                        src={card.screenSrc}
                        alt=""
                        width={1024}
                        height={630}
                        css={styles.screenImage}
                      />
                    ) : (
                      <Text css={styles.screenPlaceholder}>{card.screenLabel}</Text>
                    )}
                  </Box>
                  {card.avatarSrc && card.bubbleKey ? (
                    <Box css={styles.bubble}>
                      {/* eslint-disable-next-line @next/next/no-img-element -- 면접관 CDN 캐릭터 */}
                      <img src={card.avatarSrc} alt="" css={styles.avatar} />
                      <Text css={styles.bubbleText}>
                        {t(card.bubbleKey, card.bubbleDefault ?? '')}
                      </Text>
                    </Box>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  section: css({
    position: 'relative',
    background: LANDING_COLOR.dark,
    padding: `0 ${LANDING_SPACE.pageX} ${LANDING_SPACE.sectionBottom}`,
  }),
  cards: css({
    maxWidth: 1200,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: LANDING_SPACE.sectionGap,
  }),
  cardsTitle: css({
    margin: 0,
    color: '#fff',
    fontSize: LANDING_FONT.sectionTitle,
    fontWeight: 800,
    lineHeight: 1.25,
    letterSpacing: '-0.035em',
    textAlign: 'center',
    wordBreak: 'keep-all',
  }),
  cardList: css({
    display: 'flex',
    flexDirection: 'column',
    gap: LANDING_SPACE.cardListGap,
  }),
  card: css({
    display: 'grid',
    gridTemplateColumns: '.86fr 1.14fr',
    alignItems: 'end',
    borderRadius: LANDING_SPACE.cardRadius,
    overflow: 'hidden',
    padding: `${LANDING_SPACE.featureCardPadTop} 0 0 ${LANDING_SPACE.featureCardPadLeft}`,
    border: '1px solid rgba(255,255,255,.08)',
    transition: 'border-color 180ms ease',
    '&:hover': {
      borderColor: 'rgba(255,255,255,.32)',
    },
    [LANDING_MOBILE_MQ]: {
      gridTemplateColumns: '1fr',
      padding: `${LANDING_SPACE.featureCardPadTop} ${LANDING_SPACE.featureCardPadLeft} 0`,
      alignItems: 'stretch',
    },
  }),
  cardCopy: css({
    display: 'flex',
    flexDirection: 'column',
    gap: LANDING_SPACE.stackGap,
    paddingBottom: LANDING_SPACE.featureCardPadBottom,
    paddingRight: LANDING_SPACE.stackGap,
    [LANDING_MOBILE_MQ]: {
      paddingRight: 0,
    },
  }),
  cardLabel: css({
    fontSize: LANDING_FONT.label,
    fontWeight: 700,
  }),
  cardTitle: css({
    margin: 0,
    color: '#fff',
    fontSize: LANDING_FONT.featureCardTitle,
    fontWeight: 800,
    lineHeight: 1.25,
    letterSpacing: '-0.035em',
    wordBreak: 'keep-all',
  }),
  cardDesc: css({
    color: 'rgba(255,255,255,.6)',
    fontSize: LANDING_FONT.bodyMd,
    lineHeight: 1.75,
    wordBreak: 'keep-all',
  }),
  pills: css({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  }),
  pill: css({
    display: 'inline-flex',
    alignItems: 'center',
    height: 28,
    padding: `0 ${LANDING_SPACE.chipPadX}`,
    borderRadius: 999,
    fontSize: LANDING_FONT.pill,
    fontWeight: 700,
    letterSpacing: '0.02em',
  }),
  screenWrap: css({
    position: 'relative',
    minHeight: LANDING_SPACE.screenHeight,
  }),
  iconBadge: css({
    position: 'absolute',
    top: -16,
    right: LANDING_SPACE.badgeInset,
    zIndex: 2,
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: MUTE_LABEL_COLOR,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 20px rgba(0,204,170,.28)',
    [LANDING_MOBILE_MQ]: {
      width: 48,
      height: 48,
    },
  }),
  screen: css({
    height: LANDING_SPACE.screenHeight,
    borderRadius: '18px 0 0 0',
    background: '#fff',
    boxShadow: '0 -12px 40px rgba(17,19,21,.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    [LANDING_MOBILE_MQ]: {
      borderRadius: '18px 18px 0 0',
    },
  }),
  screenImage: css({
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    objectPosition: 'center',
    display: 'block',
  }),
  screenPlaceholder: css({
    color: LANDING_COLOR.caption,
    fontSize: LANDING_FONT.caption,
    fontWeight: 600,
  }),
  bubble: css({
    position: 'absolute',
    left: -56,
    bottom: 74,
    zIndex: 2,
    display: 'flex',
    alignItems: 'flex-end',
    gap: 10,
    maxWidth: 310,
    [LANDING_MOBILE_MQ]: {
      left: 8,
      bottom: 24,
      maxWidth: '92%',
    },
  }),
  avatar: css({
    width: 64,
    height: 64,
    borderRadius: '50%',
    objectFit: 'cover',
    objectPosition: 'top',
    background: '#E8ECF5',
    flexShrink: 0,
    [LANDING_MOBILE_MQ]: {
      width: 48,
      height: 48,
    },
  }),
  bubbleText: css({
    maxWidth: 230,
    padding: `${LANDING_SPACE.bubblePadY} ${LANDING_SPACE.bubblePadX}`,
    borderRadius: 16,
    background: '#fff',
    boxShadow: '0 10px 28px rgba(17,19,21,.16)',
    fontSize: LANDING_FONT.caption,
    fontWeight: 600,
    lineHeight: 1.45,
    color: LANDING_COLOR.ink,
    wordBreak: 'keep-all',
  }),
};
